import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { Line, Doughnut } from "react-chartjs-2";
import {
  Chart,
  ArcElement,
  CategoryScale,
  Filler,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import Head from "../../layout/head/Head";
import Content from "../../layout/content/Content";
import { Icon } from "../../components/Component";
import { membershipTypes } from "../../constants";
import { getDashboardDetail } from "../../slice/dashboard";
import "./Dashboard.css";

Chart.register(
  ArcElement,
  CategoryScale,
  Filler,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
);

// Firebase console by default; set REACT_APP_GOOGLE_ANALYTICS_URL to open a
// specific GA4 property/report instead.
const GOOGLE_ANALYTICS_URL =
  process.env.REACT_APP_GOOGLE_ANALYTICS_URL ||
  "https://console.firebase.google.com/project/miss-independent-ba7c8/analytics";

const ROLE_COLORS = [
  "#339980",
  "#f4bd0e",
  "#09c2de",
  "#9d72ff",
  "#e85347",
  "#8094ae",
];

const formatNumber = (value) => (value ?? 0).toLocaleString("en-US");

const roleLabel = (role) =>
  membershipTypes.find((t) => t.value === role)?.label ?? role;

/// Percentage change vs the previous period; null when there is no baseline.
const percentChange = (current, previous) => {
  if (!previous) return null;
  return Math.round(((current - previous) / previous) * 100);
};

const KpiCard = ({ icon, tone, label, value, hint, trend, loading }) => (
  <div className="dash-card dash-kpi">
    <div className="dash-kpi__head">
      <span className="dash-kpi__label">{label}</span>
      <span className={`dash-kpi__icon dash-tone--${tone}`}>
        <Icon name={icon}></Icon>
      </span>
    </div>
    {loading ? (
      <span className="dash-skeleton dash-skeleton--value"></span>
    ) : (
      <span className="dash-kpi__value">{formatNumber(value)}</span>
    )}
    <div className="dash-kpi__foot">
      {trend != null && (
        <span
          className={`dash-trend ${trend >= 0 ? "dash-trend--up" : "dash-trend--down"}`}
        >
          <Icon name={trend >= 0 ? "arrow-up" : "arrow-down"}></Icon>
          {Math.abs(trend)}%
        </span>
      )}
      <span>{hint}</span>
    </div>
  </div>
);

const SignupsChart = ({ signups }) => {
  const data = useMemo(
    () => ({
      labels: signups.map((d) => moment(d.date).format("DD MMM")),
      datasets: [
        {
          data: signups.map((d) => d.count),
          borderColor: "#339980",
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          pointHoverBackgroundColor: "#339980",
          tension: 0.35,
          fill: true,
          backgroundColor: (ctx) => {
            const { chart } = ctx;
            if (!chart.chartArea) return "rgba(51, 153, 128,0.1)";
            const g = chart.ctx.createLinearGradient(
              0,
              chart.chartArea.top,
              0,
              chart.chartArea.bottom,
            );
            g.addColorStop(0, "rgba(51, 153, 128,0.22)");
            g.addColorStop(1, "rgba(51, 153, 128,0)");
            return g;
          },
        },
      ],
    }),
    [signups],
  );

  const options = {
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#1f2b3a",
        padding: 10,
        displayColors: false,
        callbacks: { label: (ctx) => `${ctx.parsed.y} signups` },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: { color: "#8094ae", font: { size: 11 }, maxTicksLimit: 8 },
      },
      y: {
        beginAtZero: true,
        border: { display: false },
        grid: { color: "#eef0f5" },
        ticks: { color: "#8094ae", font: { size: 11 }, precision: 0 },
      },
    },
  };

  return (
    <div className="dash-chart">
      <Line data={data} options={options} />
    </div>
  );
};

const RoleBreakdown = ({ roles, total }) => {
  const data = {
    labels: roles.map((r) => roleLabel(r.role)),
    datasets: [
      {
        data: roles.map((r) => r.count),
        backgroundColor: roles.map(
          (_, i) => ROLE_COLORS[i % ROLE_COLORS.length],
        ),
        borderWidth: 0,
        hoverOffset: 4,
      },
    ],
  };
  const options = {
    maintainAspectRatio: false,
    cutout: "72%",
    plugins: {
      legend: { display: false },
      tooltip: { backgroundColor: "#1f2b3a", padding: 10 },
    },
  };

  return (
    <div className="dash-roles">
      <div className="dash-roles__chart">
        <Doughnut data={data} options={options} />
        <div className="dash-roles__center">
          <span className="dash-roles__total">{formatNumber(total)}</span>
          <span className="dash-roles__caption">users</span>
        </div>
      </div>
      <ul className="dash-roles__legend">
        {roles.map((r, i) => (
          <li key={r.role}>
            <span
              className="dash-roles__dot"
              style={{ background: ROLE_COLORS[i % ROLE_COLORS.length] }}
            ></span>
            <span className="dash-roles__name">{roleLabel(r.role)}</span>
            <span className="dash-roles__count">{formatNumber(r.count)}</span>
            <span className="dash-roles__pct">
              {total ? Math.round((r.count / total) * 100) : 0}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

/// Clickable shortcut card. `href` opens in a new tab, `to` is an app route.
const ActionCard = ({ icon, tone, title, description, value, to, href }) => {
  const body = (
    <>
      <span className={`dash-action__icon dash-tone--${tone}`}>
        <Icon name={icon}></Icon>
      </span>
      <span className="dash-action__text">
        <span className="dash-action__title">
          {title}
          {value != null && (
            <span className="dash-action__badge">{formatNumber(value)}</span>
          )}
        </span>
        <span className="dash-action__desc">{description}</span>
      </span>
      <Icon
        name={href ? "external" : "chevron-right"}
        className="dash-action__arrow"
      ></Icon>
    </>
  );
  return href ? (
    <a
      className="dash-card dash-action"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
    >
      {body}
    </a>
  ) : (
    <Link
      className="dash-card dash-action"
      to={`${process.env.PUBLIC_URL}${to}`}
    >
      {body}
    </Link>
  );
};

const Dashboard = () => {
  const { data, loading } = useSelector((state) => state.dashboard);
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getDashboardDetail());
  }, [dispatch]);

  const isLoading = loading && !data;
  const total = data?.totalUsers ?? 0;
  const signups = data?.signups ?? [];
  const roles = data?.usersByRole ?? [];
  const growth = percentChange(
    data?.newUsersLast30Days,
    data?.newUsersPrevious30Days,
  );
  const paidShare = total
    ? Math.round(((data?.paidUsers ?? 0) / total) * 100)
    : 0;

  return (
    <React.Fragment>
      <Head title="Dashboard"></Head>
      <Content>
        <div className="dash">
          <div className="dash-header">
            <div>
              <h3 className="dash-header__title">Dashboard</h3>
              <p className="dash-header__sub">
                Community overview · {moment().format("dddd, DD MMM YYYY")}
              </p>
            </div>
            <a
              className="dash-header__btn"
              href={GOOGLE_ANALYTICS_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon name="bar-chart"></Icon>
              <span>Google Analytics</span>
            </a>
          </div>

          <div className="dash-grid dash-grid--kpi">
            <KpiCard
              icon="users"
              tone="primary"
              label="Total Users"
              value={data?.totalUsers}
              trend={growth}
              hint={
                data?.newUsersLast30Days != null
                  ? `${formatNumber(data.newUsersLast30Days)} joined in 30 days`
                  : "All registered members"
              }
              loading={isLoading}
            />
            <KpiCard
              icon="user-add"
              tone="success"
              label="New Today"
              value={data?.newUsers}
              hint="Signed up since midnight"
              loading={isLoading}
            />
            <KpiCard
              icon="activity-round"
              tone="info"
              label="Active (24h)"
              value={data?.activeUsers}
              hint="Online in the last 24 hours"
              loading={isLoading}
            />
            <KpiCard
              icon="star"
              tone="warning"
              label="Paid Users"
              value={data?.paidUsers}
              hint={`${paidShare}% of all users`}
              loading={isLoading}
            />
          </div>

          <div className="dash-grid dash-grid--main">
            <div className="dash-card dash-panel">
              <div className="dash-panel__head">
                <div>
                  <h6 className="dash-panel__title">New signups</h6>
                  <span className="dash-panel__sub">Last 30 days</span>
                </div>
                {data?.newUsersLast30Days != null && (
                  <span className="dash-panel__stat">
                    {formatNumber(data.newUsersLast30Days)}
                  </span>
                )}
              </div>
              {isLoading ? (
                <span className="dash-skeleton dash-skeleton--chart"></span>
              ) : signups.length > 0 ? (
                <SignupsChart signups={signups} />
              ) : (
                <div className="dash-empty">No signup data yet</div>
              )}
            </div>

            <div className="dash-card dash-panel">
              <div className="dash-panel__head">
                <div>
                  <h6 className="dash-panel__title">Users by role</h6>
                  <span className="dash-panel__sub">Membership breakdown</span>
                </div>
              </div>
              {isLoading ? (
                <span className="dash-skeleton dash-skeleton--chart"></span>
              ) : roles.length > 0 ? (
                <RoleBreakdown roles={roles} total={total} />
              ) : (
                <div className="dash-empty">No role data yet</div>
              )}
            </div>
          </div>

          <div className="dash-grid dash-grid--actions">
            <ActionCard
              icon="user-check"
              tone="warning"
              title="Pending approvals"
              value={data?.pendingApprovals}
              description="Review members waiting for approval"
              to="/users"
            />
            <ActionCard
              icon="clock"
              tone="danger"
              title="Dormant users"
              value={data?.dormantUsers}
              description="No activity for more than 3 months"
              to="/users"
            />
            <ActionCard
              icon="growth"
              tone="primary"
              title="Google Analytics"
              description="App traffic, engagement and events"
              href={GOOGLE_ANALYTICS_URL}
            />
          </div>
        </div>
      </Content>
    </React.Fragment>
  );
};
export default Dashboard;
